import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

const STORAGE_KEY = "vyaahar_nav_open_sections_v1";

export default function Sidebar({ collapsed, onToggle }) {
    const loc = useLocation();
    const { user } = useAuth();
    const isSuperAdmin = ((user?.role || "").trim().toLowerCase().replace("_", " ")) === "super admin";

    // Filter out super-admin-only items for non-super-admins, then drop empty sections.
    const navFiltered = useMemo(() => {
        return NAV
            .map((g) => ({ ...g, items: g.items.filter((it) => !it.superAdminOnly || isSuperAdmin) }))
            .filter((g) => g.items.length > 0);
    }, [isSuperAdmin]);

    // Determine which section contains the active route, so we can auto-open it
    const activeSection = useMemo(() => {
        for (const group of navFiltered) {
            if (!group.section) continue;
            for (const it of group.items) {
                if (loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to))) {
                    return group.section;
                }
            }
        }
        return null;
    }, [loc.pathname, navFiltered]);

    const [openSections, setOpenSections] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
            if (stored && typeof stored === "object") return stored;
        } catch { /* ignore */ }
        // Default: only the section of the current route is open
        const init = {};
        for (const g of navFiltered) {
            if (g.section) init[g.section] = false;
        }
        return init;
    });

    // Auto-open active section when route changes — only if user has stored preferences.
    // For a fresh session (no localStorage), keep all sections collapsed by default.
    useEffect(() => {
        try {
            if (!localStorage.getItem(STORAGE_KEY)) return;
        } catch { return; }
        if (activeSection) {
            setOpenSections((s) => (s[activeSection] ? s : { ...s, [activeSection]: true }));
        }
    }, [activeSection]);

    // Persist
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(openSections)); } catch { /* ignore */ }
    }, [openSections]);

    const toggleSection = (name) => setOpenSections((s) => ({ ...s, [name]: !s[name] }));

    return (
        <aside
            data-testid="app-sidebar"
            className={cn(
                "h-screen sticky top-0 bg-sidebar text-sidebar-foreground border-r border-sidebar-border flex flex-col transition-[width] duration-200 z-30",
                collapsed ? "w-[64px]" : "w-[244px]"
            )}
            style={{ background: "hsl(var(--sidebar-bg))", color: "hsl(var(--sidebar-foreground))" }}
        >
            <div className={cn("flex items-center px-3 h-[64px] border-b", collapsed ? "justify-center px-0" : "")}
                style={{ borderColor: "hsl(var(--sidebar-border))" }}>
                {collapsed ? (
                    <img
                        src="/vyaahar-logo.png"
                        alt="Vyaahar"
                        className="h-7 object-contain"
                        style={{ objectPosition: "left center", width: "26px", objectFit: "cover", objectPositionX: "0%" }}
                    />
                ) : (
                    <div className="flex flex-col leading-tight">
                        <img
                            src="/vyaahar-logo.png"
                            alt="Vyaahar by Vyastics Technologies Private Limited"
                            className="h-7 object-contain object-left"
                            style={{ maxWidth: "180px" }}
                        />
                        <span className="mt-1 text-[9.5px] font-display italic tracking-tight"
                              style={{ color: "hsl(39 50% 65%)" }}>
                            Finance, books &amp; compliance — in one system.
                        </span>
                    </div>
                )}
            </div>
            <nav className="flex-1 overflow-y-auto px-2 py-3">
                {navFiltered.map((group, gi) => {
                    // Top-level (no section heading) — always rendered (eg Dashboard)
                    if (!group.section) {
                        return (
                            <div key={gi} className="mb-1">
                                {group.items.map((it) => (
                                    <SidebarLink key={it.to} item={it} collapsed={collapsed} active={isActive(loc.pathname, it.to)} />
                                ))}
                            </div>
                        );
                    }
                    const isOpen = collapsed ? true : !!openSections[group.section];
                    const sectionHasActive = group.section === activeSection;
                    return (
                        <div key={gi} className="mb-1">
                            {!collapsed && (
                                <button
                                    type="button"
                                    onClick={() => toggleSection(group.section)}
                                    aria-expanded={isOpen}
                                    data-testid={`nav-section-${group.section.toLowerCase()}`}
                                    className={cn(
                                        "w-full flex items-center justify-between px-3 pt-3 pb-1.5 text-[9.5px] uppercase tracking-[0.18em] font-semibold rounded-md transition-colors",
                                        sectionHasActive ? "" : "hover:bg-white/5"
                                    )}
                                    style={{ color: "hsl(var(--sidebar-muted))" }}
                                >
                                    <span>{group.section}</span>
                                    <ChevronDown
                                        className={cn("w-3 h-3 transition-transform duration-150", !isOpen && "-rotate-90")}
                                        strokeWidth={2}
                                    />
                                </button>
                            )}
                            <div
                                className={cn(
                                    "overflow-hidden transition-[max-height] duration-200",
                                    isOpen ? "max-h-[480px]" : "max-h-0"
                                )}
                            >
                                {group.items.map((it) => (
                                    <SidebarLink key={it.to} item={it} collapsed={collapsed} active={isActive(loc.pathname, it.to)} />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </nav>
            <button
                onClick={onToggle}
                data-testid="sidebar-toggle"
                className="border-t border-sidebar-border p-3 flex items-center justify-center hover:text-gold transition-colors"
                style={{ color: "hsl(var(--sidebar-muted))", borderColor: "hsl(var(--sidebar-border))" }}
            >
                {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4 mr-2" /><span className="text-[12px] font-medium">Collapse</span></>}
            </button>
        </aside>
    );
}

function isActive(pathname, to) {
    return pathname === to || (to !== "/" && pathname.startsWith(to));
}

function SidebarLink({ item, collapsed, active }) {
    const Icon = item.icon;
    return (
        <NavLink
            to={item.to}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={cn(
                "sidebar-item flex items-center gap-3 px-3 py-1.5 mx-1 text-[12.5px] font-medium",
                collapsed && "justify-center px-2"
            )}
            style={{ color: active ? "hsl(var(--sidebar-foreground))" : "hsl(var(--sidebar-muted))" }}
            title={item.label}
        >
            <Icon className="w-[17px] h-[17px] shrink-0" strokeWidth={1.6} />
            {!collapsed && <span className="truncate">{item.label}</span>}
        </NavLink>
    );
}
